import Cta from 'creative-ui/uikit/Cta';
import CtaContainer from 'creative-ui/uikit/CtaContainer';
import Modal from 'creative-ui/uikit/Modal';
import SimpleContact from 'creative-ui/uikit/SimpleContact';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import conseillerSolidSvg from '@/assets/conseillerSolid.svg';
import questionsSvg from '@/assets/questions.svg';
import { Flex } from '@/components/Layout/Flex/Flex';
import { Text } from '@/components/Typography/Text/Text';
import { commonTransComponents } from '@/i18n/translationsComponents';
import { getContactAvailability } from '@/utils/helpers/contactAvailability';

export interface ContactAdvisorModalProps {
  /** Label of the button that opens the modal, also used as the modal title. */
  ctaLabel: string;
  /** Extra class name(s) applied to the trigger button's `CtaContainer`. */
  className?: string;
}

/**
 * Ghost button that opens a modal with an advisor contact card
 * (phone number, availability, opening hours). Shared across MFA screens
 * where the user has no more access to their contact method.
 */
export function ContactAdvisorModal({
  ctaLabel,
  className,
}: Readonly<ContactAdvisorModalProps>) {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);

  const contactAvailabilityStatus = getContactAvailability();

  return (
    <>
      <CtaContainer className={className} stack>
        <Cta type="ghost-inline" onClick={() => setIsOpen(true)}>
          {ctaLabel}
        </Cta>
      </CtaContainer>

      <Modal
        open={isOpen}
        onClickClose={() => setIsOpen(false)}
        modalName={t('contactAdvisorModal.title')}>
        <h1 className="u-heading-3 u-textCenter u-margin-b-l">
          {t('contactAdvisorModal.title')}
        </h1>

        <Flex
          justify="center"
          direction="row"
          align="center"
          className="u-margin-b-l">
          <img src={questionsSvg} alt="" width="50" height="50" />
        </Flex>

        <div className="c-wysiwyg u-margin-b-l">
          <Text>
            <Trans
              i18nKey="contactAdvisorModal.intro"
              t={t}
              components={commonTransComponents}
            />
          </Text>
          <Text>
            <Trans
              i18nKey="contactAdvisorModal.description"
              t={t}
              components={commonTransComponents}
            />
          </Text>
        </div>

        <SimpleContact
          href="tel:0900000000"
          linkLabel={t('contactAdvisorModal.phoneLabel')}
          text1={t('contactAdvisorModal.text1')}
          text2={t('contactAdvisorModal.text2')}
          availability={{
            label: t(
              contactAvailabilityStatus === 'open'
                ? 'contactAdvisorModal.availability.openLabel'
                : 'contactAdvisorModal.availability.closedLabel',
            ),
            status: contactAvailabilityStatus,
          }}
          img={conseillerSolidSvg}
        />
      </Modal>
    </>
  );
}

export default ContactAdvisorModal;
